import React, { useMemo } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  Stack,
  TextField,
  Button,
  IconButton,
  Autocomplete,
  SelectChangeEvent,
} from '@mui/material';
import {
  DateRange as DateRangeIcon,
  Fingerprint as FingerprintIcon,
  Public as PublicIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

export interface FilterOptions {
  dateRange: string;
  biometricStatus: string;
  archiveStatus: string;
  country?: string;
  customStartDate?: string;
  customEndDate?: string;
}

interface ClientFilterProps {
  filters: FilterOptions;
  onFiltersChange: (filters: FilterOptions) => void;
  onApply?: () => void;
  onClear?: () => void;
  onClose?: () => void;
  showActiveFilters?: boolean;
  totalCount?: number;
  visibleCount?: number;
  countryOptions?: string[];
}

export const DEFAULT_FILTERS: FilterOptions = {
  dateRange: 'all',
  biometricStatus: 'all',
  archiveStatus: 'active',
  country: 'all',
  customStartDate: undefined,
  customEndDate: undefined,
};

const DATE_RANGES = [
  { value: 'all', label: 'All Time' },
  { value: '30', label: 'Last 30 days' },
  { value: '60', label: 'Last 60 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '120', label: 'Last 120 days' },
  { value: '150', label: 'Last 150 days' },
  { value: '180', label: 'Last 180 days' },
  { value: '365', label: 'Last year' },
  { value: 'custom', label: 'Custom range' },
];

const BIOMETRIC_OPTIONS = [
  { value: 'all', label: 'All clients' },
  { value: 'taken', label: 'Fingerprints taken' },
  { value: 'pending', label: 'Fingerprints pending' },
];

const ClientFilter: React.FC<ClientFilterProps> = ({
  filters,
  onFiltersChange,
  onApply,
  onClear,
  onClose,
  showActiveFilters = true,
  totalCount,
  visibleCount,
  countryOptions = [],
}) => {
  const handleDateRangeChange = (event: SelectChangeEvent) => {
    onFiltersChange({ ...filters, dateRange: event.target.value });
  };
  const handleBiometricStatusChange = (event: SelectChangeEvent) => {
    onFiltersChange({ ...filters, biometricStatus: event.target.value });
  };
  const handleCustomStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      customStartDate: e.target.value,
      dateRange: e.target.value ? 'custom' : filters.dateRange,
    });
  };
  const handleCustomEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      customEndDate: e.target.value,
      dateRange: e.target.value ? 'custom' : filters.dateRange,
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({ ...DEFAULT_FILTERS });
    onClear?.();
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.dateRange !== 'all') count++;
    if (filters.biometricStatus !== 'all') count++;
    if (filters.country && filters.country !== 'all') count++;
    return count;
  };

  const sortedCountryOptions = useMemo(
    () => ['all', ...[...countryOptions].sort((a, b) => a.localeCompare(b))],
    [countryOptions],
  );

  const getDateRangeLabel = () => {
    if (filters.dateRange === 'custom') {
      const start = filters.customStartDate ? new Date(filters.customStartDate).toLocaleDateString() : '';
      const end = filters.customEndDate ? new Date(filters.customEndDate).toLocaleDateString() : '';
      if (start && end) return `${start} – ${end}`;
      if (start) return `From ${start}`;
      if (end) return `Until ${end}`;
      return 'Custom range';
    }
    return DATE_RANGES.find((r) => r.value === filters.dateRange)?.label || 'All Time';
  };

  const activeCount = getActiveFiltersCount();

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 4,
          py: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.3,
            }}
          >
            Filters
          </Typography>
          {activeCount > 0 && (
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {activeCount} {activeCount === 1 ? 'active filter' : 'active filters'}
            </Typography>
          )}
        </Box>
        {onClose && (
          <IconButton
            onClick={onClose}
            aria-label="Close filters"
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Body (scrollable) */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 4, py: 4 }}>
        <Stack spacing={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Application Date Range</InputLabel>
            <Select
              value={filters.dateRange}
              onChange={handleDateRangeChange}
              label="Application Date Range"
              startAdornment={<DateRangeIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />}
            >
              {DATE_RANGES.map((range) => (
                <MenuItem key={range.value} value={range.value}>
                  {range.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {filters.dateRange === 'custom' && (
            <Box
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: '#F9FAFB',
              }}
            >
              <Typography sx={{ mb: 2, fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
                Custom date range
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={filters.customStartDate || ''}
                  onChange={handleCustomStartDateChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={filters.customEndDate || ''}
                  onChange={handleCustomEndDateChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
            </Box>
          )}

          <FormControl fullWidth size="small">
            <InputLabel>Biometric Status</InputLabel>
            <Select
              value={filters.biometricStatus}
              onChange={handleBiometricStatusChange}
              label="Biometric Status"
              startAdornment={<FingerprintIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />}
            >
              {BIOMETRIC_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            size="small"
            options={sortedCountryOptions}
            value={filters.country || 'all'}
            onChange={(_e, val) =>
              onFiltersChange({ ...filters, country: val || 'all' })
            }
            getOptionLabel={(opt) => (opt === 'all' ? 'All countries' : opt)}
            isOptionEqualToValue={(opt, val) => opt === val}
            disableClearable
            renderInput={(params) => (
              <TextField
                {...params}
                label="Country"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <PublicIcon sx={{ ml: 0.5, mr: 0.5, fontSize: 18, color: 'text.secondary' }} />
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Typography sx={{ fontSize: '0.875rem' }}>
                  {option === 'all' ? 'All countries' : option}
                </Typography>
              </Box>
            )}
          />
        </Stack>

        {showActiveFilters && activeCount > 0 && (
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography
              sx={{
                mb: 2,
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              Active filters
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {filters.dateRange !== 'all' && (
                <Chip
                  label={`Date: ${getDateRangeLabel()}`}
                  onDelete={() =>
                    onFiltersChange({
                      ...filters,
                      dateRange: 'all',
                      customStartDate: undefined,
                      customEndDate: undefined,
                    })
                  }
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              )}
              {filters.biometricStatus !== 'all' && (
                <Chip
                  label={`Biometric: ${
                    BIOMETRIC_OPTIONS.find((o) => o.value === filters.biometricStatus)?.label
                  }`}
                  onDelete={() => onFiltersChange({ ...filters, biometricStatus: 'all' })}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              )}
              {filters.country && filters.country !== 'all' && (
                <Chip
                  label={`Country: ${filters.country}`}
                  onDelete={() => onFiltersChange({ ...filters, country: 'all' })}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              )}
            </Stack>
          </Box>
        )}
      </Box>

      {/* Sticky footer */}
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: '#FFFFFF',
        }}
      >
        {typeof totalCount === 'number' && typeof visibleCount === 'number' && (
          <Box
            sx={{
              px: 4,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: '#F9FAFB',
            }}
          >
            <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
              Showing{' '}
              <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                {visibleCount}
              </Box>{' '}
              of {totalCount} {totalCount === 1 ? 'client' : 'clients'}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            px: 4,
            py: 3,
          }}
        >
          <Button
            variant="text"
            onClick={clearAllFilters}
            disabled={activeCount === 0}
            sx={{ flex: 1 }}
          >
            Clear All
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => onApply?.()}
            sx={{ flex: 2 }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ClientFilter;
